/**
 * MietwagenVertragWizard — 6-Schritt-Assistent für Mietvertrag & Abtretungserklärung
 *
 * Refactoring von MietwagenVertragAbtretung (3 Schritte) zu einem vollständigen
 * Wizard mit Sticky-Progress-Bar, Click-Navigation, Validierungs-State und
 * localStorage-Draft-Speicherung.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { FileText, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { getMietvertragStoragePath } from '@/lib/mietwagen';
import {
  isStepValid,
  buildWizardDatenFromAkte,
  buildWizardDatenFromGutachtenAkte,
  isGutachtenTotalschaden,
  saveDraft,
  loadDraft,
  clearDraft,
  EMPTY_WIZARD_DATEN,
  type StepNumber,
  type WizardDaten,
} from '@/lib/mietwagen-wizard-validation';
import WizardProgressBar from './mietwagen-wizard/WizardProgressBar';
import GutachtenUebernahmeBanner from './mietwagen-wizard/GutachtenUebernahmeBanner';
import Step1Kunde from './mietwagen-wizard/Step1Kunde';
import Step2Schaden from './mietwagen-wizard/Step2Schaden';
import Step3Fahrzeuge from './mietwagen-wizard/Step3Fahrzeuge';
import Step4Konditionen from './mietwagen-wizard/Step4Konditionen';
import Step5Uebergabe from './mietwagen-wizard/Step5Uebergabe';
import Step6Unterschrift from './mietwagen-wizard/Step6Unterschrift';

interface MietwagenVertragWizardProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  akte?: Record<string, any>;
  onComplete?: (signatureData: string, pdfUrl?: string) => void;
  onClose?: () => void;
  onUpdate?: () => void;
}

function formatDatum(d?: Date | string): string {
  if (!d) return new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function MietwagenVertragWizard({ akte, onComplete, onClose, onUpdate }: MietwagenVertragWizardProps) {
  const akteId = String(akte?.id || 'new');

  const [currentStep, setCurrentStep] = useState<StepNumber>(1);
  const [daten, setDaten] = useState<WizardDaten>(() => {
    const draft = loadDraft(akteId);
    if (draft) {
      return { ...EMPTY_WIZARD_DATEN, ...draft.daten };
    }
    return akte ? buildWizardDatenFromAkte(akte) : EMPTY_WIZARD_DATEN;
  });
  const [draftRestored] = useState(() => loadDraft(akteId) !== null);

  // Gutachten-Akte cross-link state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [gutachtenAkte, setGutachtenAkte] = useState<Record<string, any> | null>(null);
  const [isLinkLoading, setIsLinkLoading] = useState(false);
  const gutachtenFetched = useRef(false);

  const [isGenerating, setIsGenerating] = useState(false);

  // Signature data (not stored in WizardDaten to avoid serialization issues)
  const [sigVertrag, setSigVertrag] = useState('');
  const [sigAbtretung, setSigAbtretung] = useState('');
  const [sigUebergabe, setSigUebergabe] = useState('');

  // Restore step from draft and notify user about potentially stale draft data
  useEffect(() => {
    const draft = loadDraft(akteId);
    if (draft?.step) setCurrentStep(draft.step);
    if (draftRestored) {
      toast.info('Entwurf wiederhergestellt – ggf. veraltete Daten.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch Gutachten-Akte when akte has a gutachten_akte_id link
  useEffect(() => {
    const gutachtenAkteId = akte?.gutachten_akte_id as string | null | undefined;
    if (!gutachtenAkteId || gutachtenFetched.current) return;
    gutachtenFetched.current = true;

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('akten')
          .select(
            'id, akten_nummer, akten_nr, anspruchsteller_name, anspruchsteller_strasse, anspruchsteller_adresse, anspruchsteller_plz, anspruchsteller_ort, anspruchsteller_telefon, anspruchsteller_email, anspruchsteller_geburtsdatum, versicherung, versicherung_adresse, versicherung_plz, versicherung_ort, schadennummer, unfalldatum, unfallort, schadentag, schadenort, fahrzeug_kennzeichen, kennzeichen, fahrzeug_marke, fahrzeug_modell, fahrzeug_klasse, fahrzeug_totalschaden, reparaturdauer_tage, voraussichtliche_reparaturdauer',
          )
          .eq('id', gutachtenAkteId)
          .maybeSingle();
        if (cancelled || error || !data) return;
        setGutachtenAkte(data);
        // Only apply prefill when no draft was restored — a draft means the
        // user already worked on the form, so we preserve their edits.
        if (!draftRestored) {
          setDaten(buildWizardDatenFromGutachtenAkte(data as Record<string, unknown>));
        }
      } catch {
        // Non-critical — wizard remains usable without prefill
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerknuepfungLoesen = useCallback(async () => {
    if (!akte?.id || isLinkLoading) return;
    setIsLinkLoading(true);
    try {
      const { error } = await supabase
        .from('akten')
        .update({ gutachten_akte_id: null, updated_at: new Date().toISOString() })
        .eq('id', akte.id);
      if (error) throw error;
      setGutachtenAkte(null);
      setDaten(EMPTY_WIZARD_DATEN);
      clearDraft(akteId);
      toast.success('Verknüpfung mit Gutachten-Akte aufgehoben.');
      if (onUpdate) onUpdate();
    } catch {
      toast.error('Fehler beim Lösen der Verknüpfung — bitte erneut versuchen.');
    } finally {
      setIsLinkLoading(false);
    }
  }, [akte?.id, akteId, isLinkLoading, onUpdate]);

  // Auto-save draft on every daten change
  useEffect(() => {
    saveDraft(akteId, daten, currentStep);
  }, [daten, currentStep, akteId]);

  // Reaktiv mietdauer_tage aus mietbeginn / mietende ableiten
  useEffect(() => {
    if (daten.mietbeginn && daten.mietende) {
      const start = new Date(daten.mietbeginn);
      const end = new Date(daten.mietende);
      const tage = Math.max(0, Math.round((end.getTime() - start.getTime()) / 86400000));
      setDaten(prev => ({ ...prev, mietdauer_tage: tage }));
    }
  }, [daten.mietbeginn, daten.mietende]);

  const updateDaten = useCallback((updated: Partial<WizardDaten>) => {
    setDaten(prev => ({ ...prev, ...updated }));
  }, []);

  const handleStepClick = (step: StepNumber) => setCurrentStep(step);

  // Compute which steps are valid for progress bar indicators
  const validSteps = new Set<StepNumber>(
    ([1, 2, 3, 4, 5, 6] as StepNumber[]).filter(s => isStepValid(s, daten))
  );
  // Step 6 also needs both signatures
  if (!sigVertrag || !sigAbtretung) validSteps.delete(6);

  // ── PDF + Submit ──
  const handleSubmit = async () => {
    if (!sigVertrag || !sigAbtretung) {
      toast.error('Bitte unterschreiben Sie Mietvertrag und Abtretungserklärung.');
      return;
    }
    if (!daten.checkbox_mietbedingungen || !daten.checkbox_abtretung || !daten.checkbox_datenschutz) {
      toast.error('Bitte akzeptieren Sie alle Bedingungen.');
      return;
    }

    setIsGenerating(true);
    try {
      const unterschriftDatum = formatDatum();
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pw = doc.internal.pageSize.getWidth();
      const m = 15;
      const cw = pw - 2 * m;
      let y = 15;

      const header = () => {
        y = 15;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(232, 93, 4);
        doc.text('MietGo — eine Marke der LCS GmbH', m, y);
        y += 4;
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);
        doc.text('Geschaeftsfuehrer Andreas Koop | Rahmedestrasse 3, 58507 Luedenscheid', m, y);
        y += 3;
        doc.text('Tel: 02351 926 5740 | info@mietgo.de | mietgo.de', m, y);
        y += 2;
        doc.setDrawColor(232, 93, 4);
        doc.setLineWidth(0.8);
        doc.line(m, y, pw - m, y);
        y += 8;
      };

      // ── SEITE 1: MIETVERTRAG ──
      header();
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30);
      doc.text('Mietvertrag Unfallersatzfahrzeug', m, y);
      y += 8;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30);

      doc.setFont('helvetica', 'bold');
      doc.text('Mieter (Anspruchsteller)', m, y); y += 4;
      doc.setFont('helvetica', 'normal');
      doc.text(`Name: ${daten.mieter_vorname} ${daten.mieter_nachname}`, m, y); y += 3.5;
      doc.text(`Anschrift: ${daten.mieter_strasse}, ${daten.mieter_plz} ${daten.mieter_ort}`, m, y); y += 3.5;
      doc.text(`Telefon: ${daten.mieter_telefon} | E-Mail: ${daten.mieter_email}`, m, y); y += 3.5;
      doc.text(`Geb.-Datum: ${formatDatum(daten.mieter_geburtsdatum)} | Fuehrerschein-Nr.: ${daten.mieter_fuehrerschein_nr}`, m, y); y += 6;

      if (daten.fahrer_abweichend) {
        doc.setFont('helvetica', 'bold');
        doc.text('Fahrer (abweichend)', m, y); y += 4;
        doc.setFont('helvetica', 'normal');
        doc.text(`Name: ${daten.fahrer_vorname} ${daten.fahrer_nachname}`, m, y); y += 3.5;
        doc.text(`Fuehrerschein-Nr.: ${daten.fahrer_fuehrerschein_nr} | Geb.-Datum: ${formatDatum(daten.fahrer_geburtsdatum)}`, m, y); y += 6;
      }

      doc.setFont('helvetica', 'bold');
      doc.text('Gegnerische Versicherung', m, y); y += 4;
      doc.setFont('helvetica', 'normal');
      doc.text(daten.versicherung_name, m, y); y += 3.5;
      doc.text(`${daten.versicherung_adresse}, ${daten.versicherung_plz_ort}`, m, y); y += 3.5;
      doc.text(`Schadennr.: ${daten.schadennummer} | Unfalldatum: ${formatDatum(daten.unfalldatum)} | Unfallort: ${daten.unfallort}`, m, y); y += 6;

      doc.setFont('helvetica', 'bold');
      doc.text('Geschaedigtes Fahrzeug', m, y); y += 4;
      doc.setFont('helvetica', 'normal');
      doc.text(`Kennzeichen: ${daten.geschaedigtes_kfz_kennzeichen} | Typ: ${daten.geschaedigtes_kfz_typ} | Klasse: ${daten.geschaedigtes_kfz_klasse}`, m, y); y += 6;

      doc.setFont('helvetica', 'bold');
      doc.text('Mietfahrzeug', m, y); y += 4;
      doc.setFont('helvetica', 'normal');
      doc.text(`Kennzeichen: ${daten.miet_kfz_kennzeichen} | Typ: ${daten.miet_kfz_typ} | Klasse: ${daten.miet_kfz_klasse}`, m, y); y += 6;

      doc.setFont('helvetica', 'bold');
      doc.text('Mietkonditionen', m, y); y += 4;
      doc.setFont('helvetica', 'normal');
      doc.text(`Mietbeginn: ${formatDatum(daten.mietbeginn)} ${daten.mietbeginn_uhrzeit} Uhr | Mietende: ${formatDatum(daten.mietende)} | Dauer: ${daten.mietdauer_tage} Tage`, m, y); y += 3.5;
      doc.text(`Tagessatz: ${daten.tagessatz.toFixed(2)} EUR/Tag | Haftungsquote: ${daten.haftungsquote}%`, m, y); y += 6;

      // Mietbedingungen
      doc.setFont('helvetica', 'bold');
      doc.text('Mietbedingungen', m, y); y += 4;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      const bedingungen = [
        '1. Das Mietfahrzeug darf nur vom Mieter oder dem benannten Fahrer gefuehrt werden.',
        '2. Der Mieter haftet fuer Schaeden am Mietfahrzeug, die nicht durch den Unfallgegner verursacht wurden.',
        '3. Die Abrechnung erfolgt direkt mit der gegnerischen Haftpflichtversicherung.',
        '4. Bei Totalschaden oder wirtschaftlichem Totalschaden betraegt die max. Mietdauer 14 Tage ab Gutachtenerstellung.',
        '5. Bei Reparatur entspricht die Mietdauer der Reparaturdauer lt. Gutachten zzgl. Beschaffungszeit.',
        '6. Der Mieter ist verpflichtet, das Fahrzeug pfleglich zu behandeln und bei Rueckgabe vollgetankt zurueckzugeben.',
      ];
      bedingungen.forEach(b => {
        const lines = doc.splitTextToSize(b, cw);
        doc.text(lines, m, y);
        y += lines.length * 3 + 1;
      });
      y += 4;

      // Unterschrift
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Unterschrift Mieter (Mietvertrag)', m, y); y += 2;
      doc.setDrawColor(200);
      doc.setLineWidth(0.3);
      doc.line(m, y, m + 70, y);
      doc.addImage(sigVertrag, 'PNG', m, y - 15, 60, 15);
      y += 2;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(`${daten.mieter_vorname} ${daten.mieter_nachname}, ${unterschriftDatum}`, m, y);

      // ── SEITE 2: ABTRETUNGSERKLÄRUNG ──
      doc.addPage();
      header();
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30);
      doc.text('Abtretungserklaerung gem. §398 BGB', m, y);
      y += 10;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30);

      const abtretungsText = `Hiermit trete ich, ${daten.mieter_vorname} ${daten.mieter_nachname}, wohnhaft in ${daten.mieter_strasse}, ${daten.mieter_plz} ${daten.mieter_ort}, meine Ansprueche auf Erstattung der Mietwagenkosten aus dem Verkehrsunfall vom ${formatDatum(daten.unfalldatum)} in ${daten.unfallort} gegen die ${daten.versicherung_name} (Schadennr.: ${daten.schadennummer}) an die LCS GmbH (MietGo), Rahmedestrasse 3, 58507 Luedenscheid, ab.`;
      const aLines = doc.splitTextToSize(abtretungsText, cw);
      doc.text(aLines, m, y);
      y += aLines.length * 4 + 6;

      doc.setFont('helvetica', 'bold');
      doc.text('Umfang der Abtretung:', m, y); y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const umfang = [
        'Die Abtretung umfasst saemtliche Ansprueche auf Erstattung der Mietwagenkosten, die mir aus dem oben genannten Verkehrsunfall gegen den Unfallverursacher und/oder dessen Haftpflichtversicherung zustehen.',
        'Die Abtretung erfolgt erfuellungshalber. Soweit die Zessionarin (LCS GmbH / MietGo) Zahlung von der gegnerischen Versicherung erhaelt, bin ich von meiner Zahlungspflicht befreit.',
        'Ich versichere, dass die abgetretenen Ansprueche nicht anderweitig abgetreten, verpfaendet oder belastet sind.',
        'Die LCS GmbH (MietGo) ist berechtigt, die abgetretenen Ansprueche im eigenen Namen geltend zu machen.',
      ];
      umfang.forEach((u, i) => {
        const lines = doc.splitTextToSize(`${i + 1}. ${u}`, cw);
        doc.text(lines, m, y);
        y += lines.length * 3.5 + 3;
      });
      y += 6;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Schadendaten:', m, y); y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`Unfalldatum: ${formatDatum(daten.unfalldatum)}`, m, y); y += 3.5;
      doc.text(`Unfallort: ${daten.unfallort}`, m, y); y += 3.5;
      doc.text(`Geschaedigtes Fahrzeug: ${daten.geschaedigtes_kfz_kennzeichen} (${daten.geschaedigtes_kfz_typ})`, m, y); y += 3.5;
      doc.text(`Versicherung: ${daten.versicherung_name}`, m, y); y += 3.5;
      doc.text(`Schadennummer: ${daten.schadennummer}`, m, y); y += 10;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Unterschrift Zedent (Anspruchsteller)', m, y); y += 2;
      doc.setDrawColor(200);
      doc.line(m, y, m + 70, y);
      doc.addImage(sigAbtretung, 'PNG', m, y - 15, 60, 15);
      y += 2;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(`${daten.mieter_vorname} ${daten.mieter_nachname}, ${unterschriftDatum}`, m, y);
      y += 10;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text('Annahme durch LCS GmbH (MietGo)', m, y); y += 2;
      doc.setDrawColor(200);
      doc.line(m, y, m + 70, y);
      y += 2;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(`Andreas Koop, Geschaeftsfuehrer, ${unterschriftDatum}`, m, y);

      // ── SEITE 3: ÜBERGABEPROTOKOLL ──
      doc.addPage();
      header();
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30);
      doc.text('Uebergabe- und Rueckgabeprotokoll', m, y);
      y += 10;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30);

      doc.setFont('helvetica', 'bold');
      doc.text('Fahrzeuguebergabe', m, y); y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const uebergabeZeit = [daten.uebergabe_datum ? formatDatum(daten.uebergabe_datum) : '—', daten.uebergabe_uhrzeit].filter(Boolean).join(' ');
      doc.text(`Datum/Zeit: ${uebergabeZeit} | Ort: ${daten.uebergabe_ort || '—'}`, m, y); y += 3.5;
      doc.text(`KM-Stand: ${daten.uebergabe_km || '—'} | Tankstand: ${daten.uebergabe_tank || '—'}`, m, y); y += 3.5;
      doc.text(`Mietfahrzeug: ${daten.miet_kfz_kennzeichen} (${daten.miet_kfz_typ})`, m, y); y += 3.5;
      if (daten.uebergabe_vorschaeden) {
        const lines = doc.splitTextToSize(`Vorschaeden: ${daten.uebergabe_vorschaeden}`, cw);
        doc.text(lines, m, y);
        y += lines.length * 3.5;
      }
      y += 4;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Fahrzeugrueckgabe', m, y); y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`Datum: ${daten.rueckgabe_datum ? formatDatum(daten.rueckgabe_datum) : '—'} | Ort: ${daten.rueckgabe_ort || '—'}`, m, y); y += 3.5;
      doc.text(`KM-Stand: ${daten.rueckgabe_km || '—'} | Tankstand: ${daten.rueckgabe_tank || '—'}`, m, y); y += 8;

      doc.text('Schaeden bei Rueckgabe: _______________________________________________', m, y); y += 8;
      doc.text('Bemerkungen: _______________________________________________', m, y); y += 12;

      doc.setFont('helvetica', 'bold');
      doc.text('Unterschrift Mieter (Uebergabe/Rueckgabe)', m, y); y += 2;
      doc.setDrawColor(200);
      doc.line(m, y, m + 70, y);
      if (sigUebergabe) {
        doc.addImage(sigUebergabe, 'PNG', m, y - 15, 60, 15);
      }
      y += 2;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(`${daten.mieter_vorname} ${daten.mieter_nachname}, ${unterschriftDatum}`, m, y);

      // ── Speichern ──
      const pdfBlob = doc.output('blob');
      // Provisional filename — updated below with vertragsnummer once upload succeeds
      let filename = `Mietvertrag_Abtretung_${daten.mieter_nachname}_${daten.schadennummer || 'ohne'}.pdf`;

      if (akte?.id) {
        const storagePath = getMietvertragStoragePath(akte, filename);
        const { error: uploadError } = await supabase.storage
          .from('akten-dokumente')
          .upload(storagePath, pdfBlob, { contentType: 'application/pdf', upsert: true });

        if (!uploadError) {
          // ── Vertragsnummer generieren — nur nach erfolgreichem Upload ──
          // Calling the RPC after upload ensures the sequence number is not
          // consumed when the upload fails, which would leave the akte unnumbered
          // and create a permanent gap on retry.
          let vertragsnummer: string | null = akte?.vertragsnummer ?? null;
          if (!vertragsnummer || vertragsnummer === '') {
            const { data: vNrData, error: vNrErr } = await supabase.rpc('next_mietwagen_vertragsnummer');
            if (vNrErr) {
              console.warn('[vertragsnummer] RPC fehlgeschlagen:', vNrErr);
            } else {
              vertragsnummer = vNrData as string;
            }
          }

          if (vertragsnummer) {
            filename = `Mietvertrag_${vertragsnummer}_${daten.mieter_nachname}.pdf`;
          }

          const updatePayload: Record<string, unknown> = {
            abtretung_status: 'unterschrieben',
            abtretung_datum: new Date().toISOString().split('T')[0],
            uebergabe_fotos_urls: daten.uebergabe_fotos_urls.length > 0
              ? daten.uebergabe_fotos_urls
              : undefined,
          };
          if (vertragsnummer) updatePayload.vertragsnummer = vertragsnummer;

          const { error: aktenUpdateErr } = await supabase
            .from('akten')
            .update(updatePayload)
            .eq('id', akte.id);
          if (aktenUpdateErr) {
            toast.error('Fehler beim Aktualisieren der Akte — bitte erneut versuchen.');
            return;
          }

          // Kein Auto-Übergang zu uebergeben hier: uebergeben = Schlüsselübergabe,
          // nicht Vertragsunterzeichnung. Der Stepper-CTA "Jetzt übergeben" löst
          // den Übergang aus (Issue #726).

          toast.success('Mietvertrag & Abtretung gespeichert', { description: filename });
          clearDraft(akteId);
        } else {
          console.error('Upload error:', uploadError);
          toast.error('Upload fehlgeschlagen');
          return;
        }
      }

      // Download
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

      clearDraft(akteId);
      if (onComplete) onComplete(sigVertrag);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Fehler bei der PDF-Erstellung');
    } finally {
      setIsGenerating(false);
    }
  };

  const canSubmit =
    daten.checkbox_mietbedingungen &&
    daten.checkbox_abtretung &&
    daten.checkbox_datenschutz &&
    !!sigVertrag &&
    !!sigAbtretung;

  return (
    <div className="bg-white rounded-2xl border shadow-sm overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-r from-orange-50 to-white flex-shrink-0">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-orange-600" />
          <h2 className="text-base font-semibold">Mietvertrag & Abtretungserklärung</h2>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <span className="text-lg leading-none">&times;</span>
          </button>
        )}
      </div>

      {/* Sticky Progress Bar */}
      <WizardProgressBar
        currentStep={currentStep}
        validSteps={validSteps}
        onStepClick={handleStepClick}
      />

      {/* Gutachten-Übernahme-Banner */}
      {gutachtenAkte && (
        <GutachtenUebernahmeBanner
          gutachtenAktenNummer={
            (gutachtenAkte.akten_nr as string | null) ??
            (gutachtenAkte.akten_nummer as string | null) ??
            String(gutachtenAkte.id ?? '')
          }
          isTotalschaden={isGutachtenTotalschaden(gutachtenAkte as Record<string, unknown>)}
          onVerknuepfungLoesen={handleVerknuepfungLoesen}
          isLoading={isLinkLoading}
        />
      )}

      {/* Step Content */}
      <div className="overflow-y-auto flex-1">
        {currentStep === 1 && <Step1Kunde daten={daten} onChange={updateDaten} />}
        {currentStep === 2 && <Step2Schaden daten={daten} onChange={updateDaten} />}
        {currentStep === 3 && <Step3Fahrzeuge daten={daten} onChange={updateDaten} />}
        {currentStep === 4 && <Step4Konditionen daten={daten} onChange={updateDaten} />}
        {currentStep === 5 && <Step5Uebergabe daten={daten} onChange={updateDaten} akte={akte} />}
        {currentStep === 6 && (
          <Step6Unterschrift
            daten={daten}
            onChange={updateDaten}
            onSigVertragChange={setSigVertrag}
            onSigAbtretungChange={setSigAbtretung}
            onSigUebergabeChange={setSigUebergabe}
            sigVertrag={sigVertrag}
            sigAbtretung={sigAbtretung}
            sigUebergabe={sigUebergabe}
          />
        )}
      </div>

      {/* Footer Navigation */}
      <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50 flex-shrink-0">
        <button
          onClick={() => setCurrentStep(prev => Math.max(1, prev - 1) as StepNumber)}
          disabled={currentStep === 1}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-100 transition text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ← Zurück
        </button>

        <span className="text-xs text-gray-400">Schritt {currentStep} von 6</span>

        {currentStep < 6 ? (
          <button
            onClick={() => setCurrentStep(prev => Math.min(6, prev + 1) as StepNumber)}
            className="flex items-center gap-2 px-5 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-semibold"
          >
            Weiter →
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || isGenerating}
            className="flex items-center gap-2 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Wird erstellt…
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Unterschreiben & PDF erstellen
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
